// Schemas for routes-agent-runtime.ts (browser-safe zod). PRD §8, §8.0 ("run representative scenarios"), §8.2.
import { z } from 'zod';
import { AgentToolSchema, ChannelKindSchema } from './enums.js';

const Iso = z.string();
const Id = z.string().min(1).max(64);

// ───────── Setter context (spec §02, §09.9, §15.2) ─────────

/** Lead origin as far as the stored data tells (attribution proper is Wave 2C). */
export const LEAD_ORIGINS = ['form', 'crm', 'ctwa', 'organic_whatsapp', 'inbound_call', 'unknown'] as const;
export const LeadOriginSchema = z.enum(LEAD_ORIGINS);
export type LeadOrigin = z.infer<typeof LeadOriginSchema>;
export const LEAD_ORIGIN_LABELS: Record<LeadOrigin, string> = {
  form: 'Formulario (Meta, Google o web)',
  crm: 'CRM',
  ctwa: 'Anuncio de WhatsApp (CTWA)',
  organic_whatsapp: 'WhatsApp orgánico (el prospecto escribió primero)',
  inbound_call: 'Llamada entrante',
  unknown: 'Origen desconocido',
};

/** Spanish by default; English when the prospect uses it (spec §09.9). */
export const PREFERRED_LANGUAGES = ['es', 'en'] as const;
export const PreferredLanguageSchema = z.enum(PREFERRED_LANGUAGES);
export type PreferredLanguage = z.infer<typeof PreferredLanguageSchema>;
export const PREFERRED_LANGUAGE_LABELS: Record<PreferredLanguage, string> = { es: 'Español', en: 'Inglés' };

/** Simulated local time in «Probar agente»: the real clock, a daytime hour or a night hour (21:00–8:00). */
export const SIMULATION_CLOCKS = ['now', 'day', 'night'] as const;
export const SimulationClockSchema = z.enum(SIMULATION_CLOCKS);
export type SimulationClock = z.infer<typeof SimulationClockSchema>;
export const SIMULATION_CLOCK_LABELS: Record<SimulationClock, string> = {
  now: 'Hora real',
  day: 'De día (11:00)',
  night: 'De noche (23:00)',
};

export const GLOBAL_DRAFT_LABEL = 'Borrador global (sin publicar)';

export const LlmStateSchema = z.enum(['enabled', 'disabled', 'simulated']);
export type LlmState = z.infer<typeof LlmStateSchema>;

export const AgentRuntimeStatusSchema = z.object({
  llm: z.object({
    state: LlmStateSchema,
    provider: z.string().nullable(),
    model: z.string().nullable(),
    /** Why AI turns are disabled or simulated (Spanish, user-facing). */
    reason: z.string().nullable(),
  }),
  maxToolIterations: z.number().int(),
  /** Executable tool registry (no reservation tools). */
  tools: z.array(AgentToolSchema),
});
export type AgentRuntimeStatus = z.infer<typeof AgentRuntimeStatusSchema>;

export const ToolCallTraceSchema = z.object({
  id: z.string(),
  name: z.string(),
  args: z.unknown(),
  result: z.record(z.string(), z.unknown()),
  ok: z.boolean(),
  durationMs: z.number().int(),
  /** The guard refused the call before execution (unknown tool / invalid arguments). */
  rejected: z.enum(['unknown_tool', 'invalid_input']).nullable(),
  sideEffect: z.boolean(),
});
export type ToolCallTrace = z.infer<typeof ToolCallTraceSchema>;

export const TurnTraceSchema = z.object({
  startedAt: Iso,
  versions: z.object({
    global: z.number().int().nullable(),
    tools: z.number().int().nullable(),
    workspace: z.number().int().nullable(),
    project: z.number().int().nullable(),
    useDrafts: z.boolean(),
    /** Compiled with the unpublished global instructions draft («Probar agente», platform admins). */
    globalDraft: z.boolean().optional(),
    cacheKey: z.string(),
  }),
  prompt: z.object({
    approxTokens: z.number().int(),
    conflicts: z.array(z.object({ layer: z.string(), message: z.string() })),
    /** Compiled instructions came from the publish-time cache (PRD §8.0) instead of being compiled in the turn. */
    cached: z.boolean().optional(),
  }),
  bundle: z.object({
    key: z.string(),
    cached: z.boolean(),
    buildMs: z.number().int(),
    approxTokens: z.number().int(),
    sections: z.array(z.object({ name: z.string(), tokens: z.number().int() })),
    projectResolved: z.boolean(),
  }),
  toolCalls: z.array(ToolCallTraceSchema),
  /** Side effects performed — or, in the simulator, that WOULD be performed. */
  effects: z.array(z.string()),
  latency: z.object({ promptMs: z.number().int(), bundleMs: z.number().int(), modelMs: z.number().int(), toolsMs: z.number().int(), totalMs: z.number().int() }),
  usage: z.object({ inputTokens: z.number().int(), outputTokens: z.number().int(), cacheReadTokens: z.number().int(), cacheWriteTokens: z.number().int() }),
  iterations: z.number().int(),
  model: z.object({ provider: z.string(), model: z.string(), simulated: z.boolean() }).nullable(),
  knowledgeSearches: z.number().int(),
  discardedReason: z.string().nullable(),
  /** WhatsApp brevity guard: present when the reply exceeded the limit (one rewrite request, never dropped). */
  lengthGuard: z
    .object({
      limit: z.number().int(),
      originalChars: z.number().int(),
      rewrittenChars: z.number().int().nullable(),
      finalChars: z.number().int(),
      outcome: z.enum(['rewritten', 'still_over', 'rewrite_failed', 'skipped_budget']),
    })
    .nullable()
    .optional(),
});
export type TurnTrace = z.infer<typeof TurnTraceSchema>;
export type LengthGuardTrace = NonNullable<TurnTrace['lengthGuard']>;
export const LENGTH_GUARD_OUTCOME_LABELS: Record<LengthGuardTrace['outcome'], string> = {
  rewritten: 'Respuesta larga: se pidió una versión más breve',
  still_over: 'Respuesta larga: sigue excediendo el límite tras reescribirla (se envió de todos modos)',
  rewrite_failed: 'Respuesta larga: la reescritura falló (se envió la original)',
  skipped_budget: 'Respuesta larga: sin presupuesto para reescribirla (se envió la original)',
};

export const SIMULATION_OUTCOMES = ['replied', 'llm_disabled', 'budget_exceeded', 'discarded', 'no_reply', 'error'] as const;
export const SimulationOutcomeSchema = z.enum(SIMULATION_OUTCOMES);
export type SimulationOutcome = z.infer<typeof SimulationOutcomeSchema>;
export const SIMULATION_OUTCOME_LABELS: Record<SimulationOutcome, string> = {
  replied: 'Respondió',
  llm_disabled: 'IA no configurada',
  budget_exceeded: 'Límite de uso alcanzado',
  discarded: 'Respuesta descartada',
  no_reply: 'Sin respuesta',
  error: 'Error',
};

export const SimulationTurnSchema = z.object({
  index: z.number().int(),
  userText: z.string(),
  reply: z.string().nullable(),
  outcome: SimulationOutcomeSchema,
  notice: z.string().nullable(),
  at: Iso,
  trace: TurnTraceSchema.nullable(),
});
export type SimulationTurn = z.infer<typeof SimulationTurnSchema>;

export const QualificationFactSchema = z.object({
  value: z.string(),
  source: z.string(),
  confidence: z.number().nullable(),
  evidenceRef: z.string().nullable(),
  at: z.string().nullable(),
  channel: z.string().nullable().optional(),
});

export const SimulationSandboxSchema = z.object({
  qualification: z.record(z.string(), QualificationFactSchema),
  preferredLanguage: PreferredLanguageSchema.nullable().optional(),
  handoffs: z.array(z.object({ category: z.string(), reason: z.string(), recommendedNextStep: z.string(), at: Iso })),
  optedOut: z.object({ channel: z.string(), at: Iso }).nullable(),
});

export const SimulationSessionSchema = z.object({
  id: Id,
  /** Project chosen when starting; null simulates a shared number without a known project. */
  projectId: Id.nullable(),
  /** Project the session is bound to now (null until resolved). */
  boundProjectId: Id.nullable(),
  projectName: z.string().nullable(),
  channel: ChannelKindSchema,
  useDrafts: z.boolean(),
  /** Platform admins only: compiled with the unpublished GLOBAL instructions draft (never used by live replies). */
  globalDraft: z.boolean(),
  clock: SimulationClockSchema,
  leadOrigin: LeadOriginSchema,
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  createdAt: Iso,
  updatedAt: Iso,
  turns: z.array(SimulationTurnSchema),
  sandbox: SimulationSandboxSchema,
});
export type SimulationSession = z.infer<typeof SimulationSessionSchema>;

export const SimulationSummarySchema = z.object({
  id: Id,
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  channel: ChannelKindSchema,
  useDrafts: z.boolean(),
  globalDraft: z.boolean(),
  turns: z.number().int(),
  updatedAt: Iso,
});

export const CreateSimulationSchema = z.object({
  projectId: Id.nullable().optional(),
  channel: ChannelKindSchema.default('whatsapp'),
  useDrafts: z.boolean().default(false),
  /** Platform prompt admins only (403 otherwise): preview the unpublished global instructions draft. */
  globalDraft: z.boolean().default(false),
  clock: SimulationClockSchema.default('now'),
  leadOrigin: LeadOriginSchema.default('unknown'),
  contactName: z.string().trim().max(120).optional(),
  contactEmail: z.email().max(254).optional(),
});
export const SimulationListQuerySchema = z.object({ projectId: Id.optional() });
export const SendSimulationMessageSchema = z.object({ text: z.string().trim().min(1).max(2000) });

/** Simple, deterministic assertions over a scenario run. */
export const ScenarioExpectationSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('must_call_tool'), tool: AgentToolSchema }),
  z.object({ type: z.literal('must_not_call_tool'), tool: AgentToolSchema }),
  z.object({ type: z.literal('no_price_without_tool') }),
  z.object({ type: z.literal('no_booking_claim_without_confirmation') }),
  z.object({ type: z.literal('no_knowledge_search') }),
  z.object({ type: z.literal('reply_contains'), text: z.string().min(1).max(200) }),
  z.object({ type: z.literal('reply_not_contains'), text: z.string().min(1).max(200) }),
  /** v1 learning: the expected behaviour in the team's words, judged by a model over the whole run («Cumple / No cumple»). */
  z.object({ type: z.literal('behaviour'), criterion: z.string().trim().min(3).max(500) }),
]);
export type ScenarioExpectation = z.infer<typeof ScenarioExpectationSchema>;
export const SCENARIO_EXPECTATION_LABELS: Record<ScenarioExpectation['type'], string> = {
  must_call_tool: 'Debe usar la herramienta',
  must_not_call_tool: 'No debe usar la herramienta',
  no_price_without_tool: 'No menciona precios sin consultar get_current_price',
  no_booking_claim_without_confirmation: 'No afirma una cita confirmada sin confirmación del proveedor',
  no_knowledge_search: 'Responde sin búsqueda en documentos',
  reply_contains: 'La respuesta incluye',
  reply_not_contains: 'La respuesta no incluye',
  behaviour: 'Comportamiento esperado',
};

export const SaveScenarioSchema = z.object({
  name: z.string().trim().min(2).max(120),
  expectations: z.array(ScenarioExpectationSchema).max(20).default([]),
  mustPass: z.boolean().default(false),
});
export const UpdateScenarioSchema = z.object({ mustPass: z.boolean() });

export const TEST_RUN_STATUSES = ['pending', 'passed', 'failed', 'error'] as const;
export const TestRunStatusSchema = z.enum(TEST_RUN_STATUSES);
export const TEST_RUN_STATUS_LABELS: Record<z.infer<typeof TestRunStatusSchema>, string> = { pending: 'En curso', passed: 'Aprobado', failed: 'Falló', error: 'Error' };

export const ScenarioAssertionSchema = z.object({ expectation: ScenarioExpectationSchema, passed: z.boolean(), detail: z.string().nullable() });

export const TestRunSchema = z.object({
  id: Id,
  scenarioId: Id,
  scenarioName: z.string(),
  status: TestRunStatusSchema,
  useDrafts: z.boolean(),
  globalVersion: z.number().int().nullable(),
  toolVersion: z.number().int().nullable(),
  /** Project instructions version the run used (draft or published) — what a must-pass gate checks. */
  promptVersionId: Id.nullable(),
  createdAt: Iso,
  error: z.string().nullable(),
  assertions: z.array(ScenarioAssertionSchema),
  turns: z.array(SimulationTurnSchema),
});
export type TestRun = z.infer<typeof TestRunSchema>;

export const TestScenarioSchema = z.object({
  id: Id,
  name: z.string(),
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  channel: ChannelKindSchema,
  messages: z.array(z.string()),
  expectations: z.array(ScenarioExpectationSchema),
  /** «Debe pasar antes de activar cambios»: publishing the project's instructions requires a passing run of the draft. */
  mustPass: z.boolean(),
  sourceMessageId: Id.nullable(),
  createdAt: Iso,
  lastRun: TestRunSchema.omit({ turns: true }).nullable(),
});
export type TestScenario = z.infer<typeof TestScenarioSchema>;

export const TestScenarioListQuerySchema = z.object({ projectId: Id.optional() });
export const RunScenariosSchema = z.object({
  projectId: Id.optional(),
  scenarioIds: z.array(Id).max(20).optional(),
  useDrafts: z.boolean().default(false),
});

// ─────────── Conversation summaries (PRD §8.1) ───────────
// Conversation.summary holds this JSON (v1). The summary covers messages up to and including
// `coveredThroughMessageId`; every later message stays in the agent context verbatim.
export const SummaryItemSchema = z.object({
  text: z.string().max(400),
  /** Message ids that support the item (only ids of summarized messages survive validation). */
  evidence: z.array(z.string().max(64)).max(8).default([]),
  /** Project the item is about; null = not project-specific. Items of other projects never reach a project's agent. */
  projectId: z.string().max(64).nullable().default(null),
});
export type SummaryItem = z.infer<typeof SummaryItemSchema>;

export const ConversationSummaryContentSchema = z.object({
  v: z.literal(1),
  coveredThroughMessageId: z.string(),
  coveredThroughAt: Iso,
  messagesCovered: z.number().int(),
  facts: z.array(SummaryItemSchema).max(30),
  openQuestions: z.array(SummaryItemSchema).max(15),
  commitments: z.array(SummaryItemSchema).max(15),
  nextStep: z.string().max(400).nullable(),
  generatedAt: Iso,
  model: z.string().nullable(),
  simulatedModel: z.boolean().default(false),
});
export type ConversationSummaryContent = z.infer<typeof ConversationSummaryContentSchema>;

/** Parses Conversation.summary: structured v1 JSON, or null for legacy plain text / anything invalid. */
export function parseConversationSummary(raw: string | null | undefined): ConversationSummaryContent | null {
  if (!raw || raw[0] !== '{') return null;
  try {
    const r = ConversationSummaryContentSchema.safeParse(JSON.parse(raw));
    return r.success ? r.data : null;
  } catch {
    return null;
  }
}

/** Plain-text rendering of a structured summary for API consumers, webhooks and CRM notes (Spanish headings). */
export function renderConversationSummaryText(c: ConversationSummaryContent): string {
  const block = (title: string, items: SummaryItem[]) => (items.length ? [`${title}:`, ...items.map((i) => `- ${i.text}`)] : []);
  return [
    ...block('Datos', c.facts),
    ...block('Preguntas abiertas', c.openQuestions),
    ...block('Compromisos', c.commitments),
    ...(c.nextStep ? [`Siguiente paso: ${c.nextStep}`] : []),
  ].join('\n');
}

/** Readable summary from the stored value: structured v1 → rendered text; legacy plain text passes through. */
export function conversationSummaryText(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const parsed = parseConversationSummary(raw);
  if (parsed) return renderConversationSummaryText(parsed) || null;
  return raw[0] === '{' ? null : raw;
}

// ─────────── Latency trace (PRD §8.2, A29) ───────────
export const LatencyStatsQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(90).default(7),
  projectId: Id.optional(),
});

const Percentiles = z.object({ p50: z.number().int().nullable(), p95: z.number().int().nullable() });
export const LatencyBucketSchema = z.object({
  turns: z.number().int(),
  totalMs: Percentiles,
  compileMs: Percentiles,
  bundleMs: Percentiles,
  modelMs: Percentiles,
  toolsMs: Percentiles,
});
export type LatencyBucket = z.infer<typeof LatencyBucketSchema>;

export const AgentLatencyStatsSchema = z.object({
  windowDays: z.number().int(),
  /** Live AI turns that reached the model in the window (simulations excluded). */
  turns: z.number().int(),
  /** Answered from the prepared context: no tool call. */
  ordinary: LatencyBucketSchema,
  /** At least one tool call. */
  toolBacked: LatencyBucketSchema,
  /** Share of turns whose compiled instructions / context bundle came from the cache; null when unknown. */
  promptCacheHitRate: z.number().nullable(),
  bundleCacheHitRate: z.number().nullable(),
  /** Turns that ran a knowledge search (A29: ordinary questions should not). */
  knowledgeSearchTurns: z.number().int(),
  /** Sample was capped at the most recent turns. */
  truncated: z.boolean(),
});
export type AgentLatencyStats = z.infer<typeof AgentLatencyStatsSchema>;
